import { normalizePhone, phoneSearchDigits, type ClientDto, type ClientListItem } from '@kominiarz/shared';
import { Phone, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AddressField } from '../../components/AddressField';
import { Alert } from '../../components/Alert';
import { Button } from '../../components/Button';
import { ClientPicker } from '../../components/ClientPicker';
import { ChoiceGroup } from '../../components/form/ChoiceGroup';
import { TextInput } from '../../components/form/TextInput';
import { NewClientDialog } from '../../components/NewClientDialog';
import { PinControl } from '../../components/PinControl';
import { useClient, useClientSearch, useDebounced } from '../../lib/clientsApi';
import styles from './visits.module.css';

export interface PlaceState {
  clientId: number | null;
  clientName: string | null;
  objectId: number | null;
  address: string;
  lat: number | null;
  lng: number | null;
  phone: string;
}

interface Props {
  value: PlaceState;
  onChange: (patch: Partial<PlaceState>) => void;
  errors: { address?: string; phone?: string; clientId?: string; objectId?: string };
}

const OTHER = 'inny';

/**
 * Client, address and phone of a visit. Picking a client offers their addresses;
 * a client can be added on the spot; a phone typed without a client suggests the
 * matching client from the database.
 */
export function VisitPlaceFields({ value: s, onChange, errors }: Props) {
  const client = useClient(s.clientId);
  const [newClientQuery, setNewClientQuery] = useState<string | null>(null);
  const [typingOther, setTypingOther] = useState(false);
  const objects = client.data?.client.objects ?? [];
  const clientPhone = client.data?.client.phones[0]?.phone ?? null;

  // A visit opened from a client card (?klient=…&obiekt=…) gets the address once the client loads.
  useEffect(() => {
    if (!client.data || !s.objectId || s.address) return;
    const o = client.data.client.objects.find((x) => x.id === s.objectId);
    if (o) onChange({ address: o.address, lat: o.lat, lng: o.lng });
  }, [client.data, s.objectId, s.address, onChange]);

  // A phone number typed without a client: is it someone we know?
  const digits = phoneSearchDigits(normalizePhone(s.phone) ?? '');
  const phoneQuery = useDebounced(!s.clientId && digits.length >= 11 ? digits.slice(2) : '');
  const phoneMatch = useClientSearch(phoneQuery, { enabled: phoneQuery.length > 0, limit: 3 });
  const suggested = phoneQuery ? (phoneMatch.data?.clients ?? []) : [];

  function selectClient(c: Pick<ClientListItem, 'id' | 'name' | 'objects' | 'phones'>) {
    const only = c.objects.length === 1 ? c.objects[0]! : null;
    // An address typed before choosing the client is kept if it is one of theirs.
    const typed = s.address.trim().toLowerCase();
    const matching = c.objects.find((o) => o.address.toLowerCase() === typed) ?? null;
    const chosen = matching ?? (s.address.trim() ? null : only);
    setTypingOther(Boolean(s.address.trim()) && !chosen);
    onChange({
      clientId: c.id,
      // A client saved with only a phone number goes by that number.
      clientName: c.name ?? c.phones[0]?.phone ?? null,
      objectId: chosen?.id ?? null,
      ...(chosen ? { address: chosen.address, lat: null, lng: null } : {}),
    });
  }

  function onClientCreated(c: ClientDto) {
    setNewClientQuery(null);
    selectClient(c);
  }

  const choice = s.objectId ? String(s.objectId) : typingOther || (s.clientId && s.address && objects.length > 0) ? OTHER : null;
  const showAddressInput = !s.clientId || objects.length === 0 || choice === OTHER;
  const selectedObject = objects.find((o) => o.id === s.objectId);
  const coords = s.lat != null && s.lng != null ? { lat: s.lat, lng: s.lng } : null;

  return (
    <>
      {s.clientId ? (
        <div className={styles.clientCard}>
          <div className={styles.clientText}>
            <span className={styles.clientCardLabel}>Klient</span>
            <strong>{s.clientName ?? client.data?.client.name ?? 'Klient usunięty'}</strong>
            {clientPhone && <span className={styles.clientPhone}>{clientPhone}</span>}
          </div>
          <Button
            variant="ghost"
            icon={<X aria-hidden="true" />}
            onClick={() => {
              setTypingOther(Boolean(s.address));
              onChange({ clientId: null, clientName: null, objectId: null });
            }}
          >
            Zmień
          </Button>
        </div>
      ) : (
        <ClientPicker
          label="Klient"
          hint="Opcjonalnie. Wpisz nazwisko, telefon albo ulicę."
          error={errors.clientId}
          onSelect={selectClient}
          onCreate={(q) => setNewClientQuery(q)}
        />
      )}

      {s.clientId && objects.length > 0 && (
        <ChoiceGroup
          legend="Adres wizyty"
          value={choice}
          minOptionWidth="14rem"
          options={[
            ...objects.map((o) => ({
              value: String(o.id),
              label: (
                <span className={styles.objectOption}>
                  {o.label && <span className={styles.objectLabel}>{o.label}</span>}
                  {o.address}
                </span>
              ),
            })),
            { value: OTHER, label: 'Inny adres' },
          ]}
          onChange={(v) => {
            if (v === OTHER) {
              setTypingOther(true);
              onChange({ objectId: null, address: '', lat: null, lng: null });
            } else {
              const o = objects.find((x) => String(x.id) === v)!;
              setTypingOther(false);
              onChange({ objectId: o.id, address: o.address, lat: null, lng: null });
            }
          }}
          error={errors.objectId ?? (!showAddressInput ? errors.address : undefined)}
        />
      )}
      {selectedObject?.note && (
        <Alert kind="info" title="Uwagi do adresu">
          {selectedObject.note}
        </Alert>
      )}

      {selectedObject && (
        <PinControl
          address={selectedObject.address}
          coords={coords ?? (selectedObject.lat != null && selectedObject.lng != null ? { lat: selectedObject.lat, lng: selectedObject.lng } : null)}
          onPin={(c) => onChange({ lat: c.lat, lng: c.lng })}
        />
      )}

      {showAddressInput && (
        <AddressField
          label={s.clientId && objects.length > 0 ? 'Inny adres' : 'Adres'}
          hint={s.clientId ? 'Nowy adres zapisze się też na karcie klienta.' : undefined}
          value={s.address}
          coords={coords}
          // A typed address no longer matches a stored pin or client address.
          onType={(address) => onChange({ address, objectId: null, lat: null, lng: null })}
          onPick={(address, c) => onChange({ address, objectId: null, lat: c.lat, lng: c.lng })}
          onPin={(c) => onChange({ lat: c.lat, lng: c.lng })}
          error={errors.address}
        />
      )}

      <TextInput
        type="tel"
        label="Telefon"
        optional
        inputMode="tel"
        autoComplete="tel"
        hint={
          s.clientId
            ? clientPhone
              ? `Zostaw puste, żeby dzwonić na numer klienta (${clientPhone}).`
              : undefined
            : undefined
        }
        value={s.phone}
        onChange={(e) => onChange({ phone: e.target.value })}
        error={errors.phone}
      />
      {suggested.length > 0 && (
        <Alert kind="info" title="Ten numer jest w bazie klientów">
          <div className={styles.suggestions}>
            {suggested.map((c) => (
              <Button
                key={c.id}
                variant="secondary"
                icon={<Phone aria-hidden="true" />}
                onClick={() => {
                  selectClient(c);
                  onChange({ phone: '' });
                }}
              >
                Wybierz: {c.name}
              </Button>
            ))}
          </div>
        </Alert>
      )}

      {newClientQuery !== null && (
        <NewClientDialog
          open
          initialQuery={newClientQuery}
          initialAddress={s.address}
          onClose={() => setNewClientQuery(null)}
          onCreated={onClientCreated}
          onPickExisting={(c) => {
            setNewClientQuery(null);
            selectClient(c);
          }}
        />
      )}
    </>
  );
}
